import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Nav from "../components/landing/Nav";
import Hero from "../components/landing/Hero";
import ValueBand from "../components/landing/ValueBand";
import PackageShowcase from "../components/landing/PackageShowcase";
import SeoCourseFeatureTile from "../components/landing/SeoCourseFeatureTile";
import PricingGrid from "../components/landing/PricingGrid";
import FAQ from "../components/landing/FAQ";
import UseCases from "../components/landing/UseCases";
import FeaturedTestimonialsStrip from "../components/landing/FeaturedTestimonialsStrip";
import VideoTestimonials from "../components/landing/VideoTestimonials";
import WrittenTestimonials from "../components/landing/WrittenTestimonials";
import TieUpLooseEndsBanner from "../components/landing/TieUpLooseEndsBanner";
import ReneeSection from "../components/landing/ReneeSection";
import NormanSection from "../components/landing/NormanSection";
import JullySection from "../components/landing/JullySection";
import FinalCTA from "../components/landing/FinalCTA";
import HustleZoneCTA from "../components/landing/HustleZoneCTA";
import SEO, { orgSchema, personSchema, websiteSchema, faqSchema } from "../components/seo/SEO";
import Footer from "../components/landing/Footer";
import AlternativeOfferSection from "../components/landing/AlternativeOfferSection";
import BookingModal from "../components/landing/BookingModal";
import ZylvieOfferModal from "../components/landing/ZylvieOfferModal";
import RegistrationModal from "../components/webinar/RegistrationModal";
import { Toaster } from "sonner";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const [packages, setPackages] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [testimonials, setTestimonials] = useState({ written: [], video: [] });

  // Booking modal (legacy CTA on plan cards / nav)
  const [bookingOpen, setBookingOpen] = useState(false);
  const [chosenPlan, setChosenPlan] = useState("");

  // Webinar registration modal
  const [regOpen, setRegOpen] = useState(false);
  const [regMode, setRegMode] = useState("closest");

  useEffect(() => {
    (async () => {
      try {
        // Try the new DB-backed CMS endpoint first; fall back to the legacy
        // static endpoint if it returns nothing yet (covers fresh installs
        // where seed-from-legacy hasn't run).
        const [p, f] = await Promise.all([
          axios.get(`${API}/packages`),
          axios.get(`${API}/faqs`),
        ]);
        setPackages(p.data.packages || []);
        setFaqs(f.data.faqs || []);

        let cms = null;
        try {
          const cmsResp = await axios.get(`${API}/cms/testimonials/`);
          if ((cmsResp.data?.counts?.written || 0) > 0 || (cmsResp.data?.counts?.video || 0) > 0) {
            cms = cmsResp.data;
          }
        } catch { /* network or 404 → fall back */ }

        if (cms) {
          setTestimonials({ written: cms.written, video: cms.video, featured: cms.featured });
        } else {
          const t = await axios.get(`${API}/testimonials`);
          setTestimonials(t.data || { written: [], video: [] });
        }
      } catch (err) {
        console.error("Failed loading site content", err);
      }
    })();
    trackEvent("landing_page_view");
  }, []);

  const openBooking = useCallback((plan = "") => {
    setChosenPlan(plan);
    setBookingOpen(true);
  }, []);

  const openRegistration = useCallback((mode) => {
    setRegMode(mode);
    setRegOpen(true);
    trackEvent(mode === "closest" ? "join_now_click" : "register_later_click");
  }, []);

  return (
    <div className="relative min-h-screen bg-ink-900 text-white bk-grain overflow-x-hidden">
      <SEO
        title="Bow Tie Kreative — Websites, Funnels & Conversion Systems"
        description="High-converting websites, sales funnels, and content systems for entrepreneurs who want their brand to work as hard as they do. Built by Ryan Perez."
        path="/"
        image="/menu/websites-hero.jpg"
        schema={[orgSchema(), personSchema(), websiteSchema(), faqSchema(faqs)]}
      />
      <Toaster
        position="top-right"
        theme="dark"
        toastOptions={{
          style: {
            background: "#0a0a0a",
            border: "1px solid rgba(212,175,55,0.3)",
            color: "#fff",
          },
        }}
      />
      <Nav onBookCall={() => openRegistration("closest")} />
      <main>
        <Hero onBookCall={() => openRegistration("closest")} />
        <ValueBand />
        <PackageShowcase />
        <PricingGrid
          packages={packages}
          onChoosePlan={(p) =>
            trackEvent("plan_checkout_click", {
              metadata: { plan: p.id, url: p.checkout_url },
            })
          }
        />
        <FAQ faqs={faqs} />
        <UseCases />
        <FeaturedTestimonialsStrip items={testimonials.featured} />
        <VideoTestimonials videos={testimonials.video} />
        <WrittenTestimonials written={testimonials.written} />
        <SeoCourseFeatureTile />
        <TieUpLooseEndsBanner source="home" />
        <NormanSection />
        <ReneeSection />
        <JullySection />
        <FinalCTA
          onJoinNow={() => openRegistration("closest")}
          onRegisterLater={() => openRegistration("later")}
        />

        <HustleZoneCTA />
        <AlternativeOfferSection
          headline="Build me a funnel instead"
          subheadline="Fully managed funnels that turn attention into revenue — without the agency price tag or DIY headaches."
          ctaText="Explore Funnels"
          ctaTo="/funnels"
        />
      </main>
      <Footer />

      <BookingModal
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        packages={packages}
        initialPackage={chosenPlan}
      />
      <RegistrationModal
        open={regOpen}
        onOpenChange={setRegOpen}
        mode={regMode}
      />
      <ZylvieOfferModal />
    </div>
  );
}
